import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Truck,
  Sparkles,
  Leaf,
  Beef,
  Milk,
  Wheat,
  Carrot,
  Apple,
  Package,
  Egg,
  Fish,
  User,
  Cherry,
  Grape,
  Banana,
  Citrus,
  Salad,
  Cookie,
  Cake,
  Coffee,
  Ham,
  Drumstick,
  IceCream,
  CakeSlice,
  Shell,
  Croissant,
  Candy,
  Wine,
  Beer,
  GlassWater,
  Soup,
  UtensilsCrossed,
  ShoppingBasket,
  Store,
  Sandwich,
  Pizza,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import PageTransition from '@/components/PageTransition';
import ProductCard from '@/components/ProductCard';
import GlobalHeader from '@/components/Header';
import { supabase } from '@/integrations/supabase/client';
import { CategoryDB } from '@/types/admin';
import { Product } from '@/types/product';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Carrot, Apple, Cherry, Grape, Banana, Citrus, Salad, Leaf,
  Beef, Ham, Drumstick, Sausage: Ham,
  Milk, Egg, IceCream, CakeSlice,
  Fish, Shell,
  Wheat, Cookie, Croissant, Candy, Cake,
  Coffee, Wine, Beer, Cup: GlassWater,
  Sandwich, Pizza, Soup, UtensilsCrossed,
  Package, ShoppingBasket, Store,
};

const tileBase =
  'rounded-3xl md:rounded-[2rem] transition-all duration-300 will-change-transform';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
};

const MobileHeader = () => {
  const { user } = useAuth();
  return (
    <header className="flex items-center justify-between px-4 pt-4 pb-2">
      <Link to="/" className="flex items-center gap-2 group">
        <span className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground">
          <Leaf className="w-5 h-5" />
        </span>
        <span className="font-heading text-base font-bold tracking-tight text-foreground">
          Фермер<span className="text-primary">Чел</span>
        </span>
      </Link>
      {user ? (
        <Link
          to="/profile"
          className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-foreground hover:bg-secondary/80 transition-colors"
          aria-label="Профиль"
        >
          <User className="w-5 h-5" />
        </Link>
      ) : (
        <Link
          to="/auth"
          className="px-4 py-2 rounded-full bg-foreground text-background text-xs font-semibold hover:bg-foreground/90 transition-colors"
        >
          Войти
        </Link>
      )}
    </header>
  );
};

interface LegalContent {
  privacy_policy: string;
  terms_of_use: string;
}

interface HomeContent {
  show_hero: boolean;
  show_categories: boolean;
  show_featured: boolean;
  hero_badge: string;
  hero_title: string;
  hero_title_accent: string;
  hero_title_end: string;
  hero_subtitle: string;
  hero_cta_text: string;
  hero_cta_secondary: string;
  hero_products: string[];
  featured_title: string;
  featured_subtitle: string;
  promo_title: string;
  promo_subtitle: string;
  promo_link: string;
  delivery_title: string;
  delivery_subtitle: string;
  story_quote: string;
}

const defaultHome: HomeContent = {
  show_hero: true,
  show_categories: true,
  show_featured: true,
  hero_badge: 'ФермерЧел · Челябинск',
  hero_title: 'Натуральное',
  hero_title_accent: 'рядом',
  hero_title_end: '',
  hero_subtitle: 'Продукты с местных ферм Челябинской области — прямо к вашему столу.',
  hero_cta_text: 'В каталог',
  hero_cta_secondary: 'Наша история',
  hero_products: [],
  featured_title: 'Свежее на ферме',
  featured_subtitle: 'Новые поступления от наших хозяйств',
  promo_title: 'Первый заказ −10%',
  promo_subtitle: 'На все фермерские корзины',
  promo_link: '/catalog',
  delivery_title: 'Доставка по Челябинску',
  delivery_subtitle: 'Привезём сегодня — ежедневно до 21:00',
  story_quote: '«Мы лично знаем каждого фермера и проверяем каждый продукт — чтобы на вашем столе был настоящий вкус».',
};

const Index = () => {
  const isMobile = useIsMobile();
  const [categories, setCategories] = useState<CategoryDB[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [home, setHome] = useState<HomeContent>(defaultHome);
  const [legal, setLegal] = useState<LegalContent>({
    privacy_policy: '# Политика конфиденциальности\n\nМы уважаем вашу конфиденциальность и обязуемся защищать персональные данные.',
    terms_of_use: '# Условия использования\n\nИспользуя наш сайт, вы соглашаетесь с настоящими условиями использования.',
  });
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [catRes, footerRes, homeRes] = await Promise.all([
        supabase.from('categories').select('*').order('sort_order'),
        supabase.from('page_content').select('content').eq('page_slug', 'footer').maybeSingle(),
        supabase.from('page_content').select('content').eq('page_slug', 'home').maybeSingle(),
      ]);
      if (!mounted) return;
      if (catRes.data) setCategories(catRes.data);

      let homeContent = defaultHome;
      if (homeRes.data?.content) {
        const c = homeRes.data.content as Record<string, unknown>;
        homeContent = {
          show_hero: c.show_hero !== false,
          show_categories: c.show_categories !== false,
          show_featured: c.show_featured !== false,
          hero_badge: (c.hero_badge as string) || defaultHome.hero_badge,
          hero_title: (c.hero_title as string) || defaultHome.hero_title,
          hero_title_accent: (c.hero_title_accent as string) || defaultHome.hero_title_accent,
          hero_title_end: (c.hero_title_end as string) || defaultHome.hero_title_end,
          hero_subtitle: (c.hero_subtitle as string) || defaultHome.hero_subtitle,
          hero_cta_text: (c.hero_cta_text as string) || defaultHome.hero_cta_text,
          hero_cta_secondary: (c.hero_cta_secondary as string) || defaultHome.hero_cta_secondary,
          hero_products: (c.hero_products as string[]) || [],
          featured_title: (c.featured_title as string) || defaultHome.featured_title,
          featured_subtitle: (c.featured_subtitle as string) || defaultHome.featured_subtitle,
          promo_title: (c.promo_title as string) || defaultHome.promo_title,
          promo_subtitle: (c.promo_subtitle as string) || defaultHome.promo_subtitle,
          promo_link: (c.promo_link as string) || defaultHome.promo_link,
          delivery_title: (c.delivery_title as string) || defaultHome.delivery_title,
          delivery_subtitle: (c.delivery_subtitle as string) || defaultHome.delivery_subtitle,
          story_quote: (c.story_quote as string) || defaultHome.story_quote,
        };
        setHome(homeContent);
      }

      // Featured products: manual selection or latest
      let prodQuery;
      if (homeContent.hero_products.length > 0) {
        prodQuery = supabase.from('products').select('*').in('id', homeContent.hero_products);
      } else {
        prodQuery = supabase
          .from('products')
          .select('*')
          .eq('in_stock', true)
          .order('created_at', { ascending: false })
          .limit(8);
      }
      const prodRes = await prodQuery;
      if (!mounted) return;
      if (prodRes.data) {
        const arr = prodRes.data as Product[];
        // preserve manual order
        if (homeContent.hero_products.length > 0) {
          arr.sort((a, b) => homeContent.hero_products.indexOf(a.id) - homeContent.hero_products.indexOf(b.id));
        }
        setProducts(arr);
      }

      if (footerRes.data?.content) {
        const c = footerRes.data.content as Record<string, unknown>;
        setLegal((prev) => ({
          privacy_policy: (c.privacy_policy as string) || prev.privacy_policy,
          terms_of_use: (c.terms_of_use as string) || prev.terms_of_use,
        }));
      }
      setLoadingProducts(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);


  const linkify = (text: string) => {
    const parts = text.split(/(https?:\/\/[^\s)]+|[\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,})/g);
    return parts.map((part, idx) => {
      if (/^https?:\/\//.test(part)) {
        return (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 break-all hover:text-primary/80"
          >
            {part}
          </a>
        );
      }
      if (/^[\w._%+-]+@[\w.-]+\.[a-zA-Z]{2,}$/.test(part)) {
        return (
          <a key={idx} href={`mailto:${part}`} className="text-primary underline underline-offset-2 break-all hover:text-primary/80">
            {part}
          </a>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const renderText = (text: string) =>
    text.split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h2 key={i} className="text-xl font-bold mb-4 break-words">{line.slice(2)}</h2>;
      if (line.startsWith('## ')) return <h3 key={i} className="text-lg font-semibold mt-4 mb-2 break-words">{line.slice(3)}</h3>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-muted-foreground mb-2 break-words leading-relaxed">{linkify(line)}</p>;
    });


  const visibleCats = categories.slice(0, 4);

  const Bento = (
    <section className="max-w-6xl w-full mx-auto px-3 md:px-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 md:auto-rows-[200px] text-foreground">
        {/* Hero */}
        <motion.div
          {...fadeUp}
          className={`${tileBase} col-span-2 md:row-span-2 bg-secondary p-6 md:p-9 flex flex-col justify-between border border-border/40 relative overflow-hidden min-h-[360px] md:min-h-0`}
        >

          <div className="relative z-10">
            <span className="inline-flex items-center gap-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-primary font-semibold mb-3 md:mb-4">
              <Sparkles className="w-3.5 h-3.5" /> {home.hero_badge}
            </span>
            <h1 className="font-bold text-[2rem] md:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground tracking-tight">
              {home.hero_title}
              {home.hero_title_accent && (
                <>
                  <br />
                  <span className="text-primary">{home.hero_title_accent}</span>
                </>
              )}
              {home.hero_title_end && <> {home.hero_title_end}</>}
            </h1>
            <p className="mt-3 md:mt-5 text-sm md:text-base lg:text-lg text-foreground/70 max-w-[360px] leading-snug">
              {home.hero_subtitle}
            </p>
          </div>
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3 relative z-10">
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 md:px-6 py-2.5 md:py-3 rounded-full font-semibold text-sm md:text-base hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
            >
              {home.hero_cta_text} <ArrowRight className="w-4 h-4" />
            </Link>
            {home.hero_cta_secondary && (
              <Link
                to="/about"
                className="text-sm font-medium text-foreground/65 hover:text-foreground transition-colors px-2 py-2"
              >
                {home.hero_cta_secondary}
              </Link>
            )}
          </div>
          <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-accent/15 blur-2xl pointer-events-none" />
        </motion.div>


        {/* Promo */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.05 }}
          className="col-span-2"
        >
          <Link
            to={home.promo_link || '/catalog'}
            className={`${tileBase} bg-primary p-6 md:p-8 flex items-center justify-between text-primary-foreground overflow-hidden relative h-full min-h-[140px] md:min-h-[160px] hover:bg-primary/95`}
          >
            <div className="relative z-10 max-w-[70%]">
              <h2 className="font-bold text-xl md:text-2xl mb-1 break-words">{home.promo_title}</h2>
              <p className="opacity-90 text-xs md:text-sm break-words">
                {home.promo_subtitle}
              </p>
            </div>
            <div className="text-5xl md:text-7xl font-black opacity-15 select-none rotate-12 absolute right-4 bottom-0">
              %
            </div>
          </Link>

        </motion.div>

        {/* Categories */}
        {visibleCats.map((cat, i) => {
          const Icon = iconMap[cat.icon] || Leaf;
          const num = String(i + 1).padStart(2, '0');
          return (
            <motion.div
              key={cat.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 + i * 0.04 }}
              whileHover={{ y: -4 }}
              className="col-span-1"
            >
              <Link
                to={`/catalog?category=${encodeURIComponent(cat.name)}`}
                className={`${tileBase} bg-secondary p-4 md:p-6 flex flex-col justify-between border border-border/40 hover:border-primary/40 hover:bg-card group h-full min-h-[150px] md:min-h-[180px]`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-foreground/40 font-bold">
                    {num}
                  </span>
                  <span
                    className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center ${
                      cat.is_eco ? 'bg-eco/15 text-eco' : 'bg-primary/10 text-primary'
                    } group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                </div>
                <div className="mt-3">
                  <h3 className="font-bold text-base md:text-lg leading-tight line-clamp-2">
                    {cat.name}
                  </h3>
                  <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground/50 group-hover:text-primary transition-colors">
                    Смотреть <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            </motion.div>
          );
        })}

        {/* Skeleton fallbacks */}
        {visibleCats.length === 0 &&
          [0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`${tileBase} bg-secondary/60 border border-border/30 min-h-[150px] md:min-h-[180px] animate-pulse`}
            />
          ))}

        {/* Delivery */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.18 }}
          className={`${tileBase} col-span-2 bg-card/70 backdrop-blur-sm p-5 md:p-7 border border-border/50 flex items-center gap-4 md:gap-5 min-h-[120px]`}
        >
          <div className="bg-primary/10 p-3 md:p-4 rounded-2xl shrink-0">
            <Truck className="w-6 h-6 md:w-7 md:h-7 text-primary" />
          </div>
          <div className="min-w-0">
            <h4 className="font-bold text-base md:text-lg">
              {home.delivery_title}
            </h4>
            <p className="text-xs md:text-sm text-foreground/65 mt-0.5">
              {home.delivery_subtitle}
            </p>
          </div>
        </motion.div>


        {/* Story tile */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.22 }}
          className={`${tileBase} col-span-2 bg-secondary/70 p-5 md:p-7 border border-border/40 flex flex-col justify-center min-h-[140px]`}
        >
          <p className="text-sm md:text-base leading-relaxed text-foreground/80 font-medium">
            {home.story_quote}
          </p>
          <Link
            to="/about"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
          >
            О нас <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

      </div>
    </section>
  );

  const Featured = (
    <section className="max-w-6xl w-full mx-auto px-3 md:px-6 mt-10 md:mt-16">
      <motion.div
        {...fadeUp}
        className="flex items-end justify-between mb-5 md:mb-7 px-1"
      >
        <div>
          <h2 className="font-bold text-2xl md:text-3xl tracking-tight">
            {home.featured_title}
          </h2>
          <p className="text-sm text-foreground/60 mt-1">
            {home.featured_subtitle}
          </p>
        </div>

        <Link
          to="/catalog"
          className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
        >
          Все товары <ArrowRight className="w-4 h-4" />
        </Link>
      </motion.div>

      {loadingProducts ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspect-[3/4] rounded-2xl bg-secondary/60 animate-pulse"
            />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-3xl bg-secondary/60 p-10 text-center text-sm text-foreground/60">
          Товары появятся совсем скоро
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}

      <div className="md:hidden mt-5 flex justify-center">
        <Link
          to="/catalog"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
        >
          Все товары <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );

  const DarkCTA = (
    <section className="max-w-6xl mx-auto px-3 md:px-6 mt-10 md:mt-16">
      <motion.div
        {...fadeUp}
        className="rounded-3xl md:rounded-[2rem] bg-foreground p-7 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6 text-background relative overflow-hidden"
      >
        <div className="absolute -top-10 -right-10 w-72 h-72 bg-primary/25 rounded-full blur-3xl pointer-events-none" />
        <div className="text-center md:text-left relative z-10">
          <h2 className="font-bold text-2xl md:text-4xl mb-2 tracking-tight">
            Готовы к покупкам?
          </h2>
          <p className="opacity-70 text-sm md:text-base">
            Более 500 позиций от локальных производителей
          </p>
        </div>
        <Link
          to="/catalog"
          className="relative z-10 inline-flex items-center gap-3 bg-background text-foreground px-8 md:px-10 py-4 md:py-5 rounded-full font-bold text-base md:text-lg hover:bg-card transition-all shadow-2xl shadow-black/20"
        >
          Перейти в каталог
          <ArrowRight className="w-5 h-5" />
        </Link>
      </motion.div>
    </section>
  );

  const FooterEl = (
    <footer className="mt-10 md:mt-16 px-3 md:px-6">
      <div className="max-w-6xl mx-auto rounded-3xl md:rounded-[2rem] bg-secondary/60 border border-border/40 px-5 md:px-8 py-6 md:py-7">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 md:gap-4">
          <div className="flex items-center justify-center md:justify-start gap-2.5">
            <span className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center text-primary">
              <Leaf className="w-4 h-4" />
            </span>
            <div className="text-xs text-muted-foreground/90">
              <p className="font-semibold text-foreground/80">ФермерЧел</p>
              <p>© {new Date().getFullYear()} · Челябинск</p>
            </div>
          </div>
          <nav className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center md:justify-end gap-x-6 gap-y-2 text-sm">
            <Link to="/about" className="min-h-[40px] flex items-center justify-center sm:justify-start text-foreground/70 hover:text-foreground transition-colors">О нас</Link>
            <Link to="/contacts" className="min-h-[40px] flex items-center justify-center sm:justify-start text-foreground/70 hover:text-foreground transition-colors">Контакты</Link>
            <Link to="/catalog" className="min-h-[40px] flex items-center justify-center sm:justify-start text-foreground/70 hover:text-foreground transition-colors">Каталог</Link>
            <button onClick={() => setPrivacyOpen(true)} className="min-h-[40px] flex items-center justify-center sm:justify-start text-foreground/70 hover:text-foreground transition-colors">
              Политика
            </button>
            <button onClick={() => setTermsOpen(true)} className="min-h-[40px] flex items-center justify-center sm:justify-start text-foreground/70 hover:text-foreground transition-colors col-span-2 sm:col-span-1">
              Условия
            </button>
          </nav>
        </div>
      </div>
      <div className="h-24 md:h-8" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
    </footer>
  );


  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col bg-background">
        {isMobile ? <MobileHeader /> : <GlobalHeader />}
        <main className={`flex-1 ${isMobile ? 'pt-2 pb-24' : 'pt-6 md:pt-10'}`}>
          {home.show_hero && Bento}
          {home.show_featured && Featured}
          {DarkCTA}
        </main>

        {FooterEl}

        <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Политика конфиденциальности</DialogTitle>
            </DialogHeader>
            <div className="py-4">{renderText(legal.privacy_policy)}</div>
          </DialogContent>
        </Dialog>

        <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Условия использования</DialogTitle>
            </DialogHeader>
            <div className="py-4">{renderText(legal.terms_of_use)}</div>
          </DialogContent>
        </Dialog>
      </div>
    </PageTransition>
  );
};

export default Index;
